import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCompanyInfo } from '../../api/config';
import Button from '../ui/Button';
import { executeKakaoShare } from '../../utils/kakaoShare';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [info, setInfo] = useState({ phone: '', email: '' });

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const data = await getCompanyInfo();
        if (data) {
          setInfo({ phone: data.phone || '', email: data.email || '' });
        }
      } catch (err) {
        console.error('Failed to load header company info', err);
      }
    };
    fetchInfo();
  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  /*
   * 메뉴는 모두 페이지 맨 위에서 열린다(맨 위로 올리는 것은 ScrollManager).
   * '회사소개'(/about) 자리는 원래 '가치' 였다. 메인 맨 아래 섹션(/#mission)으로 스크롤해 내려가 첫 메뉴가 홈 바닥에
   * 떨어졌고, 메뉴를 늘리지 않으려고 회사소개로 바꿔 그 안에서 가치를 소개한다.
   */
  const menuItems = [
    { to: '/about', label: '회사소개' },
    { to: '/use-cases', label: '적용사례' },
    { to: '/solutions', label: '솔루션' },
    { to: '/products', label: '제품소개' },
    { to: '/partners', label: '파트너' },
    // 기술노트는 검색 유입의 주 통로다. 메뉴에서 한 번에 닿아야 한다.
    { to: '/notes', label: '기술노트' },
    { to: '/demo', label: '데모' },
    { to: '/estimate', label: '예상견적' },
  ];

  return (
    <header className="h-[70px] border-b border-gray-200 fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50">
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-full">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/" onClick={closeMobileMenu}>
            {/*
              새 로고(2026-10)는 왼쪽에 정사각 타일이 붙어 있어, 예전 높이(24px)면 글자가
              14px 남짓으로 작아진다. 높이를 키워 글자 크기를 예전과 비슷하게 맞춘다.
              `?v=` 는 1년 캐시를 끊기 위한 것 — index.html 의 아이콘들과 같은 값을 쓴다.
            */}
            <img src="/gaggaun_logo_kr.svg?v=20261022" alt="가까운솔루션" className="h-7 md:h-8 w-auto" />
          </Link>
        </div>

        {/*
          Desktop Navigation — 1024px(lg)부터 펼친다.
          예전에는 768px(md)부터 펼쳐서, 아이패드 미니 세로(768px)에서 메뉴가 "적용사/례",
          "문의하/기" 처럼 한 글자씩 꺾여 깨졌다. 메뉴 8개와 문의 버튼이 한 줄에 들어가려면
          900px 가까이 필요하다. 그보다 좁으면 햄버거 메뉴를 쓴다.
        */}
        <nav className="hidden lg:flex items-center space-x-8">
          <ul className="flex space-x-8 m-0 p-0 items-center list-none">
            {menuItems.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className="text-[0.95rem] font-bold text-gray-800 hover:text-primary-500 transition-colors">
                  {item.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/contact">
                <Button variant="primary" size="sm">문의하기</Button>
              </Link>
            </li>
          </ul>
        </nav>

        {/* Hamburger Button (Mobile) */}
        <button
          className="lg:hidden flex flex-col justify-between w-[24px] h-[18px] z-[1100] focus:outline-none"
          onClick={toggleMobileMenu}
          aria-label="메뉴 열기"
        >
          <span className={`block w-full h-[2px] bg-gray-800 rounded transition-transform duration-300 ${mobileMenuOpen ? 'translate-y-[8px] rotate-45' : ''}`}></span>
          <span className={`block w-full h-[2px] bg-gray-800 rounded transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
          <span className={`block w-full h-[2px] bg-gray-800 rounded transition-transform duration-300 ${mobileMenuOpen ? '-translate-y-[8px] -rotate-45' : ''}`}></span>
        </button>

        {/* Mobile Navigation Overlay */}
        <div 
          className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-[1000] ${mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`} 
          onClick={closeMobileMenu} 
        />

        {/* Mobile Navigation Drawer */}
        <nav className={`lg:hidden fixed top-0 right-0 w-[250px] h-screen bg-white shadow-[-4px_0_20px_rgba(0,0,0,0.1)] transition-transform duration-300 z-[1050] flex flex-col ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="px-6 pt-[86px] overflow-y-auto flex-1">
            <ul className="list-none m-0 p-0">
              {menuItems.map((item) => (
                <li key={item.to} className="border-b border-gray-100 last:border-0">
                  <Link to={item.to} onClick={closeMobileMenu} className="block py-4 text-[1.1rem] font-bold text-gray-800 hover:text-primary-500 transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
              <li className="border-b border-gray-100 last:border-0">
                <Link to="/contact" onClick={closeMobileMenu} className="block py-4 text-[1.1rem] font-bold text-gray-800 hover:text-primary-500 transition-colors">
                  온라인 문의
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Bottom Anchored Action & Info */}
          <div className="px-6 pb-12 mt-auto">
            <div className="flex justify-center items-center gap-10 border-t border-gray-100 pt-8">
              <button 
                onClick={() => info.phone && (window.location.href = `tel:${info.phone}`)}
                className="flex flex-col items-center gap-2 group focus:outline-none"
                aria-label="전화 문의"
              >
                <div className="w-[50px] h-[50px] bg-green-500 rounded-2xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform p-3">
                  <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.28-.28.67-.36 1.02-.25 1.12.37 2.32.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                </div>
              </button>
              
              <button 
                onClick={() => executeKakaoShare({})}
                className="flex flex-col items-center gap-2 group focus:outline-none"
                aria-label="카카오톡 공유"
              >
                <div className="w-[50px] h-[50px] bg-[#FEE500] rounded-2xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform p-3">
                  <svg className="w-full h-full text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3C6.48 3 2 6.36 2 10.5C2 13.04 3.51 15.28 5.82 16.59L4.85 20.14C4.76 20.5 5.16 20.78 5.48 20.59L9.69 17.82C10.43 17.94 11.2 18 12 18C17.52 18 22 14.64 22 10.5C22 6.36 17.52 3 12 3Z"/>
                  </svg>
                </div>
              </button>

              <button 
                onClick={() => info.email && (window.location.href = `mailto:${info.email}`)}
                className="flex flex-col items-center gap-2 group focus:outline-none"
                aria-label="이메일 문의"
              >
                <div className="w-[50px] h-[50px] bg-blue-500 rounded-2xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform p-3">
                  <svg className="w-full h-full text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
